import { NotificationService } from '../lib/notifications';
import { ApiError } from './ApiError';
import { buildUrl } from './lib/buildUrl';
import { handleHttpError } from './lib/handleHttpError';
import { handleSuccessNotification } from './lib/handleSuccessNotification';
import { handleUnauthorizedRequest } from './lib/handleUnauthorizedRequest';
import { parseResponse } from './lib/parseResponse';
import { prepareRequest } from './lib/prepareRequest';
import { HttpMethod, RequestConfig } from './types';

export class ApiClient {
  constructor(
    private baseUrl: string,
    private notifier = NotificationService,
    private getToken: () => string | null = () => localStorage.getItem('token'),
    private onUnauthorized?: () => Promise<void>,
    private onPaymentRequired?: () => void
  ) {}

  // ========================
  // Core request
  // ========================

  private async request<T>(
    endpoint: string,
    method: HttpMethod,
    config: RequestConfig = {}
  ): Promise<T> {
    try {
      const { headers, body } = prepareRequest(method, config, this.getToken);

      let response = await fetch(
        buildUrl(this.baseUrl, endpoint, config.params),
        {
          method,
          headers,
          body,
          signal: config.signal,
        }
      );

      // if (process.env.NODE_ENV === 'development') {
      //   response = new Response(null, {
      //     status: 503,
      //     statusText: 'Service Unavailable',
      //   });
      // }

      if (response.status === 502 || response.status === 503) {
        window.location.replace('/maintenance.html');

        throw new ApiError(
          'Сервис временно недоступен',
          response.status
        );
      }

      if (!response.ok) {
        const { message, responseStatus, errorData } = await handleHttpError(
          response,
          config,
          (message) => this.notifier.error?.({ message }),
          this.onPaymentRequired
        );

        throw new ApiError(message, responseStatus, errorData);
      }

      const result = await parseResponse<T>(response, config);

      handleSuccessNotification(result, config, (message) =>
        this.notifier.success?.({ message })
      );

      return result;
    } catch (error) {
      // console.log('handleUnauthorizedRequest', 'error', error, error.status);
      if ((error as any).status === 401) {
        return handleUnauthorizedRequest(
          (endpoint, method, config) =>
            this.request<T>(endpoint, method, config),
          this.onUnauthorized,
          endpoint,
          method,
          config,
          error
        );
      }

      throw error;
    }
  }

  // ========================
  // Public API methods
  // ========================

  get<T>(endpoint: string, config?: RequestConfig) {
    return this.request<T>(endpoint, 'GET', config);
  }

  post<T>(endpoint: string, config?: RequestConfig) {
    return this.request<T>(endpoint, 'POST', config);
  }

  put<T>(endpoint: string, config?: RequestConfig) {
    return this.request<T>(endpoint, 'PUT', config);
  }

  patch<T>(endpoint: string, config?: RequestConfig) {
    return this.request<T>(endpoint, 'PATCH', config);
  }

  delete<T>(endpoint: string, config?: RequestConfig) {
    return this.request<T>(endpoint, 'DELETE', config);
  }

  // ========================
  // File download helper
  // ========================

  async download(endpoint: string, filename?: string, config?: RequestConfig) {
    const blob = await this.request<Blob>(endpoint, 'GET', {
      ...config,
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;

    // Try to auto-detect filename from header
    if (!filename) {
      filename = 'download';
    }

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  }
}
